import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import type { ThinkingStatus } from '../../pm-plugins/thinking-plugin';

/**
 * Builds the ADF prompt that triggers the suggest-edits skill. The text that
 * precedes the inline skill mention is passed in (already-localised) so the
 * prompt the AI service receives matches the user's locale.
 */
export const buildSuggestEditsPrompt = (precedingText: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [
				{ type: 'text', text: precedingText },
				{
					type: 'inlineExtension',
					attrs: {
						extensionKey: 'skill:skillTag',
						extensionType: 'com.atlassian.rovo.skill',
						parameters: { slug: 'suggest-edits' },
					},
				},
			],
		},
	],
});

type UseSkillConversationParams = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	isRovoMounted: boolean;
	thinkingStatus: ThinkingStatus | undefined;
};

/**
 * Owns the conversation lifecycle for the suggest-edits skill triggered by the
 * Thinking surface:
 *  - When `thinkingStatus` flips to `'thinking'`, create an optimistic
 *    conversation and dispatch the suggest-edits skill prompt.
 *  - Watch the conversation status and transition the editor state:
 *      idle  → complete (or → stop, if Rovo Chat is open)
 *      error → stop
 *  - When `thinkingStatus` returns to `'idle'`, abort any in-flight request
 *    and clear the active conversation id.
 *  - While Rovo is mounted and we're still thinking, surface the conversation
 *    in the Rovo chat panel via `setSelectedConversation`.
 */
export function useSkill({ api, thinkingStatus, isRovoMounted }: UseSkillConversationParams): void {
	const { formatMessage } = useIntl();
	const { createConversationOptimistically, sendMessage, abortRequests, setSelectedConversation } =
		useSmartCreationChatStoreApi();

	// Mint a stable conversation id for each thinking session during render so
	// that `useConversationById` can subscribe immediately — no state setter
	// needed. The ref is cleared by the abort effect when thinking stops.
	const conversationIdRef = useRef<string | null>(null);
	if (thinkingStatus === 'thinking' && !conversationIdRef.current) {
		conversationIdRef.current = crypto.randomUUID();
	}
	const conversationId = conversationIdRef.current;

	const activeConversation = useConversationById({ conversationId: conversationId ?? '' });

	// Kick off the suggest-edits skill when a new conversation id is minted.
	// `conversationId` only becomes non-null once per thinking session (the ref
	// guards against re-minting), so this effect fires exactly once per session.
	useEffect(() => {
		if (!conversationId) {
			return;
		}

		const promptPrefix = formatMessage(aiSuggestionsMessages.suggestEditsPromptPrefix);

		const run = async () => {
			await createConversationOptimistically({ id: conversationId });
			sendMessage({
				prompt: buildSuggestEditsPrompt(promptPrefix),
				conversationId,
				storeUserMessages: true,
				fastModeEnabled: true,
				addCurrentPageAsContextUserPreference: true,
			});
		};

		run().catch(() => {
			api?.aiSuggestions?.actions.stopThinking();
		});
	}, [conversationId, api, createConversationOptimistically, sendMessage, formatMessage]);

	// Abort the in-flight request and clear the conversation id when thinking
	// stops. The ref is used directly here because `conversationId` (derived
	// from the ref during render) is already null by the time this runs.
	useEffect(() => {
		if (thinkingStatus !== 'idle') {
			return;
		}
		if (conversationIdRef.current) {
			abortRequests?.({ conversationId: conversationIdRef.current });
			conversationIdRef.current = null;
		}
	}, [thinkingStatus, abortRequests]);

	// React to conversation status changes reported by the store and drive the
	// editor thinking state accordingly:
	//   - store idle + Rovo mounted   → stopThinking (Rovo chat takes over)
	//   - store idle + Rovo unmounted → refreshSuggestions then completeThinking
	//   - deactivatedAgentError       → stopThinking
	useEffect(() => {
		if (!activeConversation) {
			return;
		}

		switch (activeConversation.status) {
			case CHAT_STORE_STATUS.idle: {
				/**
				 * If chat store returns to idle while we're in thinking
				 * this means that the chat has been completed
				 */
				if (thinkingStatus === 'thinking') {
					if (isRovoMounted) {
						/**
						 * This means that rovo has completed
						 * Since rovo chat is open we don't show the thinking bar upon completion
						 */
						api?.aiSuggestions?.actions.stopThinking();
					} else if (activeConversation.lastAgentMessageStatus !== undefined) {
						/**
						 * The first 'idle' chat store status will have
						 * lastAgentMessageStatus undefined.
						 * If it's not undefined, it means that it is returning to idle (complete)
						 */
						api?.aiSuggestions?.actions.refreshSuggestions(false);
						api?.aiSuggestions?.actions.completeThinking();
					}
				}
				break;
			}
			case CHAT_STORE_STATUS.deactivatedAgentError: {
				api?.aiSuggestions?.actions.stopThinking();
				break;
			}
		}
	}, [activeConversation, api, isRovoMounted, thinkingStatus]);

	// When Rovo chat is open and a conversation is active, surface it in the
	// Rovo panel so the user can follow along in real time.
	useEffect(() => {
		if (isRovoMounted && thinkingStatus !== 'idle' && conversationId) {
			setSelectedConversation({ conversationId });
		}
	}, [conversationId, isRovoMounted, setSelectedConversation, thinkingStatus]);
}
